import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LifeBuoy } from "lucide-react";

/**
 * The wording the client agrees to. Kept byte-identical to
 * `TechnicalAuditService.EXPERT_HELP_CONSENT_TEXT`, because the backend stores
 * this text on the ticket as the record of what was shown — if the two drift,
 * the stored consent stops describing the dialog the client actually read.
 *
 * ⚠️ Changing this copy means bumping EXPERT_HELP_CONSENT_VERSION on the backend
 * and updating it there too. Never edit one side alone.
 */
export const EXPERT_HELP_CONSENT_TEXT =
	"This sends a request to the Agile & Co team. A specialist will review " +
	"this issue and contact you to agree what's needed. This is a separate, " +
	"human-delivered service — not one of the automated fixes in this tool. " +
	"Nothing happens to your site until you've agreed the work.";

interface ExpertHelpDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Resolves when the request has been filed. */
	onConfirm: () => Promise<void> | void;
	/** Shown for context so the client knows what they're asking help with. */
	pageUrl?: string;
	issueLabel?: string;
}

export function ExpertHelpDialog({
	open,
	onOpenChange,
	onConfirm,
	pageUrl,
	issueLabel,
}: ExpertHelpDialogProps) {
	const [submitting, setSubmitting] = useState(false);

	const handleConfirm = async () => {
		setSubmitting(true);
		try {
			await onConfirm();
			onOpenChange(false);
		} catch {
			// Keep the dialog open so the client can retry — closing it would look
			// like the request was sent. The caller's mutation surfaces the error.
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-lg">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2 text-base">
						<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-coral-light">
							<LifeBuoy className="h-4 w-4 text-coral-dark" />
						</div>
						Get expert help with this issue?
					</DialogTitle>
				</DialogHeader>

				<div className="space-y-3">
					{(issueLabel || pageUrl) && (
						<div className="rounded-md border border-border bg-muted/40 p-3 text-xs">
							{issueLabel && <div className="font-medium">{issueLabel}</div>}
							{pageUrl && (
								<div className="mt-0.5 break-all text-muted-foreground">{pageUrl}</div>
							)}
						</div>
					)}

					<p className="text-sm leading-relaxed text-foreground">
						{EXPERT_HELP_CONSENT_TEXT}
					</p>
				</div>

				<div className="mt-2 flex justify-end gap-2">
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={submitting}
					>
						No, not now
					</Button>
					<Button onClick={handleConfirm} disabled={submitting}>
						{submitting ? "Sending request…" : "Yes, request help"}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
}
